import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import Media from 'react-media'
import TabNav from 'portal-modules/common/components/tab-nav'
import { PAYMENTS } from 'portal-modules/payments/constants/urls'
import { connect } from 'react-redux'
import getProjectName from 'portal-modules/common/utils/get-project-name'
import { withRouter } from 'react-router-dom'
import { requestLedger } from 'portal-modules/ledger/redux/modules/ledger'
import { RESPONSIVE_BREAKPOINTS } from 'portal-modules/tenant-portal-layout/constants/responsive-breakpoints'
import {
  LEDGER_SCOPES,
  buildLedgerExportSections,
  countLedgerExportRows,
  getActiveScopeFromLocation,
} from 'portal-modules/ledger/utils/ledger-scope'
import { exportLedger } from 'portal-modules/ledger/utils/ledger-export'
import { LedgerDownloadDropdown } from './LedgerDownloadDropdown'
import {
  StyledH3,
  StyledTPLedgerDownloadSlot,
  StyledTPLedgerTabButton,
  StyledTPLedgerTabContainer,
  StyledTPLedgerTabNav,
  StyledTPLedgerToolbarRow,
} from './LedgerTabnav.styles'

const SCOPE_TAB_TESTIDS = {
  [LEDGER_SCOPES.FULL]: 'ledger-scope-full',
  [LEDGER_SCOPES.PAYMENTS]: 'ledger-scope-payments',
  [LEDGER_SCOPES.CHARGES]: 'ledger-scope-charges',
  [LEDGER_SCOPES.INVOICES]: 'ledger-scope-invoices',
}

// Returns `isActive` separately from the spreadable HTML attrs so callers
// don't have to read back `aria-pressed` to drive component-level props
// like styled-components' `isActive`. Single source of truth for the
// active comparison stays inside this helper.
const getScopeTab = (scope, activeScope) => {
  const isActive = scope === activeScope
  return {
    isActive,
    attrs: {
      'aria-pressed': isActive,
      'data-state': isActive ? 'active' : 'inactive',
      'data-testid': SCOPE_TAB_TESTIDS[scope],
    },
  }
}

const LedgerTabNav = ({
  type,
  showLedger,
  scheduledCharges,
  isDisplayLedgerByCharges,
  requestLedger,
  history,
  onDownload,
  filteredCount,
  totalCount,
  activeScope,
  exportSections = [],
  showSalesTax = false,
  propertyName,
  propertyBuilding,
  propertySuite,
}) => {
  useEffect(() => {
    requestLedger()
  }, [])

  const isTenantPortal = getProjectName() === 'tp'

  // Runs the client-side export pipeline (DEV-293832). The optional
  // `onDownload` callback is preserved so parent screens can layer
  // telemetry / audit hooks on top without owning the file-generation
  // logic themselves. Each tab exports its own on-screen sections /
  // columns (Full / Payments / Charges / Invoices), so the document
  // always mirrors the active tab rather than the full ledger.
  const handleDownload = useCallback(
    async format => {
      await exportLedger({
        format,
        sections: exportSections,
        scope: activeScope,
        showSalesTax,
        propertyName,
        propertyBuilding,
        propertySuite,
      })
      if (typeof onDownload === 'function') onDownload(format)
    },
    [
      onDownload,
      exportSections,
      showSalesTax,
      activeScope,
      propertyName,
      propertyBuilding,
      propertySuite,
    ],
  )

  // Handle tab navigation for tenant portal
  const handleTabNavigation = route => {
    history.push(route)
  }

  // Tenant Portal specific tab navigation with original functionality
  if (isTenantPortal) {
    const fullTab = getScopeTab(LEDGER_SCOPES.FULL, activeScope)
    const paymentsTab = getScopeTab(LEDGER_SCOPES.PAYMENTS, activeScope)
    const chargesTab = getScopeTab(LEDGER_SCOPES.CHARGES, activeScope)
    const invoicesTab = getScopeTab(LEDGER_SCOPES.INVOICES, activeScope)

    return (
      <StyledTPLedgerTabNav>
        <StyledTPLedgerToolbarRow>
          <StyledTPLedgerTabContainer>
            {showLedger && (
              <StyledTPLedgerTabButton
                {...fullTab.attrs}
                isActive={fullTab.isActive}
                onClick={() => handleTabNavigation(PAYMENTS.LEDGER.ROOT)}
              >
                {__('Full Ledger')}
              </StyledTPLedgerTabButton>
            )}
            <StyledTPLedgerTabButton
              {...paymentsTab.attrs}
              isActive={paymentsTab.isActive}
              onClick={() => handleTabNavigation(PAYMENTS.LEDGER.PAYMENTS)}
            >
              {__('Payments')}
            </StyledTPLedgerTabButton>
            {((getProjectName() === 'rp' &&
              scheduledCharges &&
              isDisplayLedgerByCharges &&
              !!Object.keys(scheduledCharges).length) ||
              (getProjectName() !== 'rp' && showLedger)) && (
              <StyledTPLedgerTabButton
                {...chargesTab.attrs}
                isActive={chargesTab.isActive}
                onClick={() => handleTabNavigation(PAYMENTS.LEDGER.CHARGES)}
              >
                {__('Charges')}
              </StyledTPLedgerTabButton>
            )}
            {'rp' !== getProjectName() && (
              <StyledTPLedgerTabButton
                {...invoicesTab.attrs}
                isActive={invoicesTab.isActive}
                onClick={() => handleTabNavigation(PAYMENTS.LEDGER.INVOICE)}
              >
                {__('Invoices')}
              </StyledTPLedgerTabButton>
            )}
          </StyledTPLedgerTabContainer>
          <StyledTPLedgerDownloadSlot>
            <LedgerDownloadDropdown
              onSelect={handleDownload}
              disabled={filteredCount === 0}
              filteredCount={filteredCount}
              totalCount={totalCount}
            />
          </StyledTPLedgerDownloadSlot>
        </StyledTPLedgerToolbarRow>
      </StyledTPLedgerTabNav>
    )
  }

  // Original tab navigation for other portals
  return (
    <Media query={`(min-width: ${RESPONSIVE_BREAKPOINTS.tablet.min}px)`}>
      {isLargeScreen => (
        <TabNav type={type}>
          {showLedger && (
            <TabNav.Item exact to={PAYMENTS.LEDGER.ROOT}>
              {__('Full Ledger')}
            </TabNav.Item>
          )}
          {getProjectName() === 'rp' ? (
            showLedger ||
            (scheduledCharges && Object.keys(scheduledCharges).length !== 0) ||
            (!isLargeScreen && Object.keys(scheduledCharges).length === 0) ? (
              <TabNav.Item exact to={PAYMENTS.LEDGER.PAYMENTS}>
                {__('Payments')}
              </TabNav.Item>
            ) : (
              <header>
                <StyledH3>{__('Payments')}</StyledH3>
              </header>
            )
          ) : (
            <TabNav.Item exact to={PAYMENTS.LEDGER.PAYMENTS}>
              {__('Payments')}
            </TabNav.Item>
          )}
          {getProjectName() === 'rp'
            ? scheduledCharges &&
              isDisplayLedgerByCharges &&
              !!Object.keys(scheduledCharges).length && (
                <TabNav.Item exact to={PAYMENTS.LEDGER.CHARGES}>
                  {__('Charges')}
                </TabNav.Item>
              )
            : showLedger && (
                <TabNav.Item exact to={PAYMENTS.LEDGER.CHARGES}>
                  {__('Charges')}
                </TabNav.Item>
              )}
          {'rp' !== getProjectName() && (
            <TabNav.Item exact to={PAYMENTS.LEDGER.INVOICE}>
              {__('Invoices')}
            </TabNav.Item>
          )}
        </TabNav>
      )}
    </Media>
  )
}

LedgerTabNav.propTypes = {
  type: PropTypes.oneOf(['header', 'content']),
  showLedger: PropTypes.bool,
  scheduledCharges: PropTypes.object,
  isDisplayLedgerByCharges: PropTypes.bool,
  requestLedger: PropTypes.func,
  history: PropTypes.object,
  onDownload: PropTypes.func,
  filteredCount: PropTypes.number,
  totalCount: PropTypes.number,
  activeScope: PropTypes.oneOf(Object.values(LEDGER_SCOPES)),
  exportSections: PropTypes.arrayOf(PropTypes.object),
  showSalesTax: PropTypes.bool,
  propertyName: PropTypes.string,
  propertyBuilding: PropTypes.string,
  propertySuite: PropTypes.string,
}

// Lines feeding the PDF property block (DEV-293832) — mirrors the
// canonical reads used by MainDrawerLargeScreenTP so the sidebar and
// the downloaded statement always agree on "what property am I on".
const buildPropertyLines = ({ properties, leases, user }) => {
  const propertyId = user?.propertyId
  const leaseId = user?.leaseId
  const property = (properties && propertyId && properties[propertyId]) || null
  const lease = (leases && leaseId && leases[leaseId]) || null
  const propertyName = property?.property_name || undefined
  const propertyBuilding = lease?.building_name
    ? __('Building {%s, 0}', [lease.building_name])
    : undefined
  const propertySuite = lease?.unit_number
    ? __('Suite {%s, 0}', [lease.unit_number])
    : undefined
  return { propertyName, propertyBuilding, propertySuite }
}

export default withRouter(
  connect(
    (
      {
        ledger: ledgerTransactions,
        customerInvoice,
        productPermissions,
        scheduledCharges,
        settings: { ledger },
        properties,
        leases,
        user,
      },
      { location },
    ) => {
      const transactionsList = ledgerTransactions
        ? Object.values(ledgerTransactions)
        : []
      const customerInvoices = customerInvoice
        ? Object.values(customerInvoice)
        : []
      const activeScope = getActiveScopeFromLocation(location?.pathname)
      // Each tab exports its own dataset: Full → all ledger rows,
      // Payments → Pending/Complete payment groups, Charges → posted
      // charges, Invoices → the customerInvoice records shown on that tab
      // (NOT ledger rows).
      const exportSections = buildLedgerExportSections({
        scope: activeScope,
        ledgerRows: transactionsList,
        customerInvoices,
      })
      // Mirror TPLedgerTable's Sales Tax rule so the Full Ledger export
      // shows the same column the tenant sees on screen.
      const showSalesTax =
        !!ledger?.tax_exclusive ||
        !!ledger?.has_tax_transactions ||
        transactionsList.some(
          row => Array.isArray(row?.taxes) && row.taxes.length > 0,
        )
      const propertyLines = buildPropertyLines({ properties, leases, user })
      return {
        showLedger:
          'rh' === getProjectName() ||
          'tp' === getProjectName() ||
          !!productPermissions.show_ledger,
        scheduledCharges,
        isDisplayLedgerByCharges:
          ledger?.display_ledger_by == 'Charges' || false,
        activeScope,
        totalCount:
          activeScope === LEDGER_SCOPES.INVOICES
            ? customerInvoices.length
            : transactionsList.length,
        filteredCount: countLedgerExportRows(exportSections),
        exportSections,
        showSalesTax,
        ...propertyLines,
      }
    },
    { requestLedger },
  )(LedgerTabNav),
)
